import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllCategories, deleteCategory, updateCategory, uploadCategoryImage } from '../api/categoryApi';
import { resolveImageUrl } from '../utils/image';
import AdminNav from './AdminNav';
import './AdminDashboard.css';

export default function AdminCategoryPage() {
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showEdit, setShowEdit] = useState(false);
  const [editCategory, setEditCategory] = useState<any>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await getAllCategories();
      const data = Array.isArray(response) ? response : response.value;
      setCategories(data || []);
    } catch (error) {
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleEdit = (cat: any) => {
    setEditCategory(cat);
    setShowEdit(true);
  };

  const handleDelete = (categoryId: number) => {
    setConfirmDeleteId(categoryId);
  };

  const confirmDelete = async () => {
    if (confirmDeleteId !== null) {
      await deleteCategory(confirmDeleteId);
      setConfirmDeleteId(null);
      fetchCategories();
    }
  };

  const cancelDelete = () => {
    setConfirmDeleteId(null);
  };

  const handleUpdate = async (updatedCat: any) => {
    await updateCategory(updatedCat);
    setShowEdit(false);
    setEditCategory(null);
    fetchCategories();
  };

  const filteredCategories = useMemo(() => {
    let list = categories;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(c => 
        (c.categoryName || '').toLowerCase().includes(q) ||
        (c.categoryDescription || '').toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => {
      const orderA = a.displayOrder && a.displayOrder > 0 ? a.displayOrder : 999999;
      const orderB = b.displayOrder && b.displayOrder > 0 ? b.displayOrder : 999999;
      if (orderA !== orderB) return orderA - orderB;
      return (a.categoryID || 0) - (b.categoryID || 0);
    });
  }, [categories, searchQuery]);

  return (
    <div className="ad-page-wrapper">
      <AdminNav activeTab="categories" />

      <main className="ad-main-content">
        <div className="ad-container">
          {/* Header */}
          <div className="ad-page-header">
            <div>
              <h1 className="ad-page-title">Categories Management</h1>
              <p className="ad-page-subtitle">Organize fireworks collections, upload category thumbnails, and manage catalog groups</p>
            </div>
            <button 
              className="ad-btn-primary-action"
              onClick={() => navigate('/admin/add-category')}
            >
              <span>+ Add New Category</span>
            </button>
          </div>

          {/* Toolbar */}
          <div className="ad-toolbar-card">
            <div className="ad-search-box">
              <span className="ad-search-icon">🔍</span>
              <input
                type="text"
                placeholder="Search category by name or description..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="ad-search-input"
              />
              {searchQuery && (
                <button className="ad-clear-btn" onClick={() => setSearchQuery('')}>✕</button>
              )}
            </div>

            <div className="ad-count-info">
              Showing <strong>{filteredCategories.length}</strong> categories
            </div>
          </div>

          {/* Table / Cards */}
          <div className="ad-table-card">
            {loading ? (
              <div className="ad-loading-box">
                <div className="ad-spinner"></div>
                <p>Loading categories...</p>
              </div>
            ) : filteredCategories.length > 0 ? (
              <>
                {/* Desktop View Table */}
                <div className="ad-desktop-table-container">
                  <table className="ad-data-table">
                    <thead>
                      <tr>
                        <th style={{ width: '70px', textAlign: 'center' }}>Rank</th>
                        <th>Thumbnail</th>
                        <th>Category Name</th>
                        <th>Description</th>
                        <th>Products Linked</th>
                        <th style={{ textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCategories.map((cat: any) => (
                        <tr key={cat.categoryID}>
                          <td style={{ textAlign: 'center' }}>
                            <span className="ad-rank-badge">
                              {cat.displayOrder && cat.displayOrder > 0 ? `#${cat.displayOrder}` : '—'}
                            </span>
                          </td>
                          <td>
                            <div className="ad-table-thumb">
                              {cat.imageUrl ? (
                                <img src={resolveImageUrl(cat.imageUrl)} alt={cat.categoryName} />
                              ) : (
                                <span>🧨</span>
                              )}
                            </div>
                          </td>
                          <td className="ad-cell-name">
                            <strong>{cat.categoryName}</strong>
                          </td>
                          <td className="ad-cell-desc">
                            {cat.categoryDescription || <span style={{ color: '#94a3b8' }}>No description</span>}
                          </td>
                          <td>
                            <span className="ad-items-pill">{cat.productCount || 0} products</span>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <div className="ad-actions-cell">
                              <button 
                                className="ad-btn-action edit"
                                onClick={() => handleEdit(cat)}
                              >
                                Edit
                              </button>
                              <button 
                                className="ad-btn-action delete"
                                onClick={() => handleDelete(cat.categoryID)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile View Card List */}
                <div className="ad-mobile-card-list">
                  {filteredCategories.map((cat: any) => (
                    <div className="ad-mobile-cat-card" key={cat.categoryID}>
                      <div className="ad-m-cat-media">
                        {cat.imageUrl ? (
                          <img src={resolveImageUrl(cat.imageUrl)} alt={cat.categoryName} />
                        ) : (
                          <span>🧨</span>
                        )}
                      </div>
                      <div className="ad-m-cat-body">
                        <div className="ad-m-cat-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span>{cat.categoryName}</span>
                          {cat.displayOrder && cat.displayOrder > 0 && (
                            <span className="ad-rank-badge">#{cat.displayOrder}</span>
                          )}
                        </div>
                        <div className="ad-m-cat-desc">{cat.categoryDescription || 'No description provided'}</div>
                        <div className="ad-m-cat-count">{cat.productCount || 0} products linked</div>
                        <div className="ad-m-cat-actions">
                          <button className="ad-btn-action edit full" onClick={() => handleEdit(cat)}>Edit</button>
                          <button className="ad-btn-action delete full" onClick={() => handleDelete(cat.categoryID)}>Delete</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="ad-empty-state">
                <div className="ad-empty-icon">📁</div>
                <h3>No Categories Found</h3>
                <p>{searchQuery ? `No categories match "${searchQuery}".` : 'No categories exist yet. Click "+ Add New Category" to create one.'}</p>
                {searchQuery && (
                  <button className="ad-filter-pill" onClick={() => setSearchQuery('')}>Clear Search</button>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Edit Category Slide-over Drawer */}
      {showEdit && editCategory && (
        <EditCategoryDrawer
          category={editCategory}
          onClose={() => { setShowEdit(false); setEditCategory(null); }}
          onUpdate={handleUpdate}
        />
      )}

      {/* Delete Confirmation Modal */}
      {confirmDeleteId !== null && (
        <div className="ad-modal-backdrop" onClick={cancelDelete}>
          <div className="ad-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="ad-confirm-icon">⚠️</div>
            <h3>Delete Category?</h3>
            <p>Are you sure you want to permanently delete this category? Associated products may become unassigned.</p>
            <div className="ad-confirm-actions">
              <button className="ad-btn-action delete full" onClick={confirmDelete}>Yes, Delete</button>
              <button className="ad-btn-action close full" onClick={cancelDelete}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Edit Category Slide-over Drawer
function EditCategoryDrawer({ category, onClose, onUpdate }: any) {
  const [name, setName] = useState(category.categoryName);
  const [desc, setDesc] = useState(category.categoryDescription || '');
  const [displayOrder, setDisplayOrder] = useState<string>(
    category.displayOrder && category.displayOrder > 0 ? String(category.displayOrder) : ''
  );
  const [imageUrl, setImageUrl] = useState(category.imageUrl || '');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>(category.imageUrl ? resolveImageUrl(category.imageUrl) : '');
  const [saving, setSaving] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selected = e.target.files[0];
      setFile(selected);
      setPreview(URL.createObjectURL(selected));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      let finalPath = imageUrl;
      if (file) {
        const uploaded = await uploadCategoryImage(file);
        finalPath = uploaded.path;
      }
      await onUpdate({
        CategoryId: category.categoryID,
        CategoryName: name,
        CategoryDescription: desc,
        ImageUrl: finalPath,
        DisplayOrder: displayOrder ? parseInt(displayOrder, 10) : 0,
      });
    } catch {
      alert('Failed to update category.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ad-modal-backdrop" onClick={onClose}>
      <div className="ad-drawer-card" onClick={e => e.stopPropagation()}>
        <div className="ad-drawer-header">
          <div className="ad-modal-title-group">
            <span className="ad-modal-tag">EDIT CATEGORY</span>
            <h2>{category.categoryName}</h2>
          </div>
          <button className="ad-modal-close" onClick={onClose}>✕</button>
        </div>

        <form className="ad-drawer-form" onSubmit={handleSubmit}>
          <div className="ad-form-field">
            <label>Category Name <span className="req">*</span></label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              required
              className="ad-input"
              placeholder="e.g. Aerial Sky Shots"
            />
          </div>

          <div className="ad-form-field">
            <label>Description</label>
            <textarea
              value={desc}
              onChange={e => setDesc(e.target.value)}
              className="ad-input textarea"
              rows={3}
              placeholder="Brief description for category card..."
            />
          </div>

          <div className="ad-form-field">
            <label>Display Order / Sequence Rank</label>
            <input
              type="number"
              min="1"
              value={displayOrder}
              onChange={e => setDisplayOrder(e.target.value)}
              className="ad-input"
              placeholder="e.g. 1 (shown 1st), 2 (shown 2nd)..."
            />
            <span className="ad-input-hint">Lower numbers appear first on customer pages.</span>
          </div>

          <div className="ad-form-field">
            <label>Category Image</label>
            {preview && (
              <div className="ad-image-preview-box">
                <img src={preview} alt="Preview" />
                <button 
                  type="button" 
                  className="ad-remove-preview-btn" 
                  onClick={() => { setFile(null); setPreview(''); setImageUrl(''); }}
                >
                  ✕
                </button>
              </div>
            )}
            <div className="ad-upload-input-wrap">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="ad-file-input"
              />
            </div>
            <div className="ad-or-divider">OR PASTE URL</div>
            <input
              type="text"
              value={imageUrl}
              onChange={e => {
                setImageUrl(e.target.value);
                setPreview(e.target.value ? resolveImageUrl(e.target.value) : '');
              }}
              className="ad-input"
              placeholder="https://example.com/image.jpg"
            />
          </div>

          <div className="ad-drawer-actions">
            <button className="ad-btn-action complete full" type="submit" disabled={saving}>
              {saving ? 'Saving Changes...' : 'Save & Update Category'}
            </button>
            <button className="ad-btn-action close full" type="button" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}